import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useSavedMeals } from "@/hooks/useSavedMeals";
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { useState } from "react";
import { CreateMealModal } from "./CreateMealModal";
import { NutritionIcons } from "./NutritionIcons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Analytics } from "@/lib/analytics";

export const SavedMealsList = () => {
  const { savedMeals, loading, deleteSavedMeal } = useSavedMeals();
  const { logFoodToMeal, fetchDailyNutrition } = useComprehensiveNutrition();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [currentMeal, setCurrentMeal] = useState<any>(null);
  const [editingMeal, setEditingMeal] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Group saved meals by meal type
  const mealsByType = {
    breakfast: savedMeals.filter(m => m.meal_type === 'breakfast'),
    lunch: savedMeals.filter(m => m.meal_type === 'lunch'),
    dinner: savedMeals.filter(m => m.meal_type === 'dinner'),
    snack: savedMeals.filter(m => m.meal_type === 'snack'),
  };

  const openAddMealDialog = (meal: any) => {
    setCurrentMeal(meal);
    setSelectedDate(new Date());
    setSelectedMealType('breakfast');
    setIsDialogOpen(true);
  };

  const openEditMealModal = (meal: any) => {
    setEditingMeal(meal);
    setShowEditModal(true);
  };

  const logSavedMeal = async () => {
    if (!currentMeal || !selectedDate) return;
    
    console.log('=== LOG SAVED MEAL START ===');
    console.log('Current meal object:', currentMeal);
    console.log('Current meal items:', currentMeal.items);
    console.log('Items array length:', currentMeal.items?.length || 0);
    console.log('Selected date:', selectedDate);
    console.log('Selected meal type:', selectedMealType);
    
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");

      if (!currentMeal.items || currentMeal.items.length === 0) {
        console.error('NO ITEMS FOUND IN SAVED MEAL!');
        toast.error("This saved meal has no items to log");
        return;
      }

      // Log each item in the saved meal
      for (const item of currentMeal.items) {
        console.log('Logging item:', item);
        
        // The saved meal items already have total nutrition for their quantity
        // We treat each item as "1 serving" where the serving is the stored quantity
        await logFoodToMeal(
          selectedMealType,
          {
            id: crypto.randomUUID(),
            name: item.food_name,
            serving_size: item.quantity, // The serving size is the stored quantity
            serving_unit: item.unit,
            calories_per_serving: item.calories, // Use total calories, not divided
            protein_per_serving: item.protein, // Use total protein, not divided
            carbs_per_serving: item.carbs, // Use total carbs, not divided
            fat_per_serving: item.fat, // Use total fat, not divided
            fiber_per_serving: item.fiber || 0, // Use total fiber, not divided
            sugar_per_serving: 0,
            sodium_per_serving: 0,
            verified: false,
            is_public: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as any,
          1, // Log 1 serving
          'serving', // Unit is "serving"
          dateStr
        );
        
        console.log('Item logged successfully');
      }

      console.log('All items logged, fetching daily nutrition');
      await fetchDailyNutrition(dateStr);
      Analytics.savedMealUsed(currentMeal.name, selectedMealType);
      toast.success(`${currentMeal.name} logged to ${selectedMealType} on ${format(selectedDate, 'MMM d, yyyy')}!`);
      setIsDialogOpen(false);
      console.log('=== LOG SAVED MEAL END ===');
    } catch (error) {
      console.error("Error logging saved meal:", error);
      toast.error("Failed to log meal");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading saved meals...
      </div>
    );
  }

  if (savedMeals.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">
            No saved meals yet. Create custom meals for quick logging!
          </p>
        </CardContent>
      </Card>
    );
  }

  const renderMealCard = (meal: any) => (
    <Card key={meal.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg">{meal.name}</CardTitle>
                {meal.description && (
                  <CardDescription className="mt-1">{meal.description}</CardDescription>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => openEditMealModal(meal)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8 flex-shrink-0">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete saved meal?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete "{meal.name}". This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteSavedMeal(meal.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <NutritionIcons
              calories={meal.total_calories}
              protein={meal.total_protein}
              carbs={meal.total_carbs}
              fat={meal.total_fat}
              className="flex-wrap"
            />

            {meal.items && meal.items.length > 0 && (
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold mb-1">Contains:</p>
                <ul className="list-disc list-inside space-y-1">
                  {meal.items.map((item) => (
                    <li key={item.id}>
                      {item.quantity} {item.unit} {item.food_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              onClick={() => openAddMealDialog(meal)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add meal
            </Button>
          </CardContent>
        </Card>
  );

  return (
    <>
      <div className="space-y-8">
        {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((type) => {
          const meals = mealsByType[type];
          if (meals.length === 0) return null;
          
          return (
            <div key={type}>
              <h3 className="text-xl font-semibold capitalize mb-4">{type}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {meals.map(renderMealCard)}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add meal to log</DialogTitle>
            <DialogDescription>
              Select the date and meal type for "{currentMeal?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date > new Date()}
                className="rounded-md border"
              />
            </div>

            <div className="space-y-3">
              <Label>Meal Type</Label>
              <RadioGroup value={selectedMealType} onValueChange={(value) => setSelectedMealType(value as typeof selectedMealType)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="breakfast" id="breakfast" />
                  <Label htmlFor="breakfast" className="font-normal cursor-pointer">Breakfast</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="lunch" id="lunch" />
                  <Label htmlFor="lunch" className="font-normal cursor-pointer">Lunch</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dinner" id="dinner" />
                  <Label htmlFor="dinner" className="font-normal cursor-pointer">Dinner</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="snack" id="snack" />
                  <Label htmlFor="snack" className="font-normal cursor-pointer">Snack</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={logSavedMeal} disabled={!selectedDate}>
              Add to {selectedMealType}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateMealModal 
        open={showEditModal} 
        onOpenChange={setShowEditModal}
        editMeal={editingMeal}
      />
    </>
  );
};